import { ApiError } from "@aurumiq/api-client";
import { toast } from "@aurumiq/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, qs, rawApi } from "@/lib/api";
import type {
  ApiKey,
  ApiKeyCreated,
  AuditItem,
  DashboardSummary,
  MailSettings,
  MailSettingsInput,
  Paginated,
  SsoSettings,
  SsoSettingsInput,
  TestMailResult,
} from "@/lib/types";

const BASE = "/api/v1/admin";

export interface AuditFilters {
  action?: string;
  actor_id?: string;
  org_id?: string;
  from?: string;
  to?: string;
  page?: number;
}

export const systemKeys = {
  audit: (f: AuditFilters) => ["admin", "audit", f] as const,
  apiKeys: ["admin", "api-keys"] as const,
  dashboard: ["admin", "dashboard"] as const,
  sso: ["admin", "settings", "sso"] as const,
  mail: ["admin", "settings", "mail"] as const,
};

export function useMailSettings() {
  return useQuery({
    queryKey: systemKeys.mail,
    queryFn: () => rawApi.get<MailSettings>(`${BASE}/settings/mail`),
  });
}

export function useSaveMailSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MailSettingsInput) =>
      rawApi.put<MailSettings>(`${BASE}/settings/mail`, input),
    onSuccess: (s) => {
      qc.setQueryData(systemKeys.mail, s);
      toast.success("E-mail settings saved");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function useTestMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (to: string) => rawApi.post<TestMailResult>(`${BASE}/settings/mail/test`, { to }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: systemKeys.mail });
      if (r.ok) toast.success(r.detail);
      else toast.error(r.detail);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function useApplyMailSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => rawApi.post<MailSettings>(`${BASE}/settings/mail/apply`),
    onSuccess: (s) => {
      qc.setQueryData(systemKeys.mail, s);
      toast.success(
        s.status.apply_mode === "watcher"
          ? "Applied. The auth service is restarting (about a minute)."
          : "Written. Restart the auth service to activate.",
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function useSsoSettings() {
  return useQuery({
    queryKey: systemKeys.sso,
    queryFn: () => rawApi.get<SsoSettings>(`${BASE}/settings/sso`),
  });
}

export function useSaveSsoSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SsoSettingsInput) => rawApi.put<SsoSettings>(`${BASE}/settings/sso`, input),
    onSuccess: (s) => {
      qc.setQueryData(systemKeys.sso, s);
      toast.success("SSO settings saved");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function useApplySsoSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => rawApi.post<SsoSettings>(`${BASE}/settings/sso/apply`),
    onSuccess: (s) => {
      qc.setQueryData(systemKeys.sso, s);
      toast.success(
        s.status.apply_mode === "watcher"
          ? "Credentials applied. The auth service is restarting (about a minute)."
          : "Credentials written. Restart the auth service to activate them.",
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function useAudit(filters: AuditFilters) {
  return useQuery({
    queryKey: systemKeys.audit(filters),
    queryFn: () => rawApi.get<Paginated<AuditItem>>(`${BASE}/audit${qs({ ...filters })}`),
  });
}

export function useApiKeys() {
  return useQuery({
    queryKey: systemKeys.apiKeys,
    queryFn: () => rawApi.get<Paginated<ApiKey>>(`${BASE}/api-keys`),
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; scopes: string[] }) =>
      rawApi.post<ApiKeyCreated>(`${BASE}/api-keys`, input),
    onSuccess: (k) => {
      qc.invalidateQueries({ queryKey: systemKeys.apiKeys });
      toast.success(`API key "${k.name}" created`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rawApi.delete<void>(`${BASE}/api-keys/${id}${qs({ reason })}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: systemKeys.apiKeys });
      toast.success("API key revoked");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

/** `null` when the endpoint is not implemented yet (404) so the dashboard can fall back. */
export function useDashboard() {
  return useQuery({
    queryKey: systemKeys.dashboard,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () => {
      try {
        return await rawApi.get<DashboardSummary>(`${BASE}/dashboard`);
      } catch (err) {
        if (err instanceof ApiError && (err.isNotFound || err.isNetwork)) return null;
        throw err;
      }
    },
  });
}
